import { useEffect, useState } from 'react';

import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import Navbar from './components/Common/Navbar';
import AccountSelector from './components/Common/AccountSelector';
import InstallPrompt from './components/Common/InstallPrompt';
import CallDashboard from './components/Calls/CallDashboard';
import LearningPanel from './components/AI/LearningPanel';
import HelpCenter from './components/AI/HelpCenter';
import VoiceEnrollment from './components/Voice/VoiceEnrollment';
import LinkDevice from './components/Auth/LinkDevice';
import useAuth from './hooks/useAuth';
import { useChat } from './hooks/useChat';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import { api } from './services/api';

type View = 'chats' | 'settings' | 'profile' | 'calls' | 'learning' | 'help' | 'voice';

const viewFromPath = (path: string): View =>
  path.startsWith('/settings')
    ? 'settings'
    : path.startsWith('/profile')
      ? 'profile'
      : path.startsWith('/calls')
          ? 'calls'
          : path.startsWith('/learning')
            ? 'learning'
            : path.startsWith('/help')
              ? 'help'
              : path.startsWith('/voice')
                ? 'voice'
                : 'chats';

const isLinkDeviceFlow = (): string | null => {
  const hash = window.location.hash;
  if (!hash.includes('link-device')) return null;
  const params = new URLSearchParams(hash.split('?')[1] ?? '');
  return params.get('token');
};

const App = () => {
  const { state } = useAuth();
  const [linkToken, setLinkToken] = useState<string | null>(() => isLinkDeviceFlow());

  useEffect(() => {
    const handleHashChange = () => setLinkToken(isLinkDeviceFlow());
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  if (linkToken) {
    return <LinkDevice token={linkToken} onSuccess={() => setLinkToken(null)} />;
  }

  if (state === 'loading') {
    return (
      <main className="app-loading" aria-busy="true">
        <div className="brand-mark" aria-hidden="true">
          AI
        </div>
        <p>AI MessageBot veilig starten…</p>
      </main>
    );
  }
  if (state === 'anonymous') return <AuthScreen />;
  return <Dashboard />;
};

const AuthScreen = () => {
  const { error: authError } = useAuth();
  const [bootstrap, setBootstrap] = useState<{
    setupRequired: boolean;
    registrationAllowed: boolean;
  } | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void api
      .bootstrap(controller.signal)
      .then((result) => {
        setBootstrap(result);
        setShowRegister(result.setupRequired);
        setLoadError(null);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setLoadError('De serverconfiguratie kon niet worden geladen.');
        }
      });
    return () => controller.abort();
  }, []);

  return (
    <main className="auth-page">
      <div className="auth-background" aria-hidden="true" />
      <div className="auth-column">
        {(authError || loadError) && (
          <p className="notice error" role="alert">
            {loadError ?? authError}
          </p>
        )}
        {registered && !showRegister && (
          <p className="notice success" role="status">
            Account aangemaakt. Je kunt nu inloggen.
          </p>
        )}
        {!bootstrap && loadError ? (
          <section className="auth-card">
            <div className="brand-mark" aria-hidden="true">
              AI
            </div>
            <h1>Backend niet bereikbaar</h1>
            <p className="muted">
              Controleer je verbinding en de geconfigureerde backend-URL.
            </p>
            <button
              type="button"
              className="button primary"
              onClick={() => window.location.reload()}
            >
              Opnieuw proberen
            </button>
          </section>
        ) : !bootstrap ? (
          <section className="auth-card" aria-busy="true">
            <div className="brand-mark" aria-hidden="true">
              AI
            </div>
            <p>Configuratie laden…</p>
          </section>
        ) : showRegister ? (
          <Register
            setupRequired={bootstrap.setupRequired}
            onBack={() => setShowRegister(false)}
            onRegistered={() => {
              setRegistered(true);
              setShowRegister(false);
              setBootstrap({ ...bootstrap, setupRequired: false });
            }}
          />
        ) : (
          <Login
            registrationAllowed={bootstrap.registrationAllowed}
            onShowRegister={() => setShowRegister(true)}
          />
        )}
      </div>
    </main>
  );
};

const Dashboard = () => {
  const {
    error,
    clearError,
    loading,
    offline,
    realtimeConnected,
    reload,
    accounts,
    chats,
    activeCalls,
  } = useChat();
  const [view, setView] = useState<View>(() => viewFromPath(window.location.pathname));
  const [styleProfile, setStyleProfile] = useState<{
    userId: string;
    messagesAnalyzed: number;
    avgLength: number;
    emojiRatio: number;
    formalRatio: number;
    commonWords: string[];
    sampleMessages: string[];
    toneDescription: string;
    language: string;
    learningLevel: number;
    avgResponseSeconds: number;
    updatedAt: string;
  } | null>(null);

  useEffect(() => {
    const handleNavigation = () => setView(viewFromPath(window.location.pathname));
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  useEffect(() => {
    if (view === 'learning') {
      void api.getStyle().then(setStyleProfile).catch(() => undefined);
    }
  }, [view]);

  const navigate = (next: View) => {
    const path = next === 'chats' ? '/' : `/${next}`;
    window.history.pushState({}, '', path);
    setView(next);
  };

  return (
    <div className="app-shell">
      <Navbar view={view} onNavigate={navigate} />
      {accounts.length > 1 && (
        <AccountSelector accounts={accounts} onActivate={(id) => void api.activateAccount(id).then(() => reload())} />
      )}
      {(offline || !realtimeConnected) && (
        <div className={`status-strip ${offline ? 'error' : 'warning'}`} role="status">
          {offline
            ? 'Je bent offline. De app-shell blijft beschikbaar; chatgegevens worden niet gecachet.'
            : 'Realtime verbinding wordt hersteld…'}
        </div>
      )}
      {error && (
        <div className="global-error" role="alert">
          <span>{error.message}</span>
          <div className="button-row">
            <button type="button" className="button link" onClick={() => void reload()}>
              Opnieuw proberen
            </button>
            <button type="button" className="button icon" onClick={clearError} aria-label="Sluiten">
              ×
            </button>
          </div>
        </div>
      )}
      {loading && <div className="loading-line" aria-label="Gegevens laden" />}
      {view === 'chats' ? <Home /> : view === 'settings' ? <Settings /> : view === 'profile' ? <Profile /> : view === 'calls' ? <CallDashboard activeCalls={activeCalls} chats={chats} onReload={() => void reload()} /> : view === 'help' ? <HelpCenter /> : view === 'voice' ? <VoiceEnrollment /> : <LearningPanel styleProfile={styleProfile} onReload={() => void api.getStyle().then(setStyleProfile)} />}
      <InstallPrompt />
    </div>
  );
};

export default App;